import {
    Box,
    Button,
    Input,
    Spinner,
    VStack,
    Text as ChakraText,
    Checkbox
} from '@chakra-ui/react';
import * as React from 'react';
import { createHash } from 'crypto';

interface FileResponse {
    fileId: string;
    name: string;
    filePath: string;
    createdAt: string;
    tags: string[] | null;
    AITags: string[] | null;
    customMetadata?: { key: string; value: string }[];
}

interface FolderResponse {
    folderPath: string;
}

interface FileObject {
    fileId: string;
    name: string;
    folderPath: string;
    filePath: string;
    createdAt: string;
    tags: string;
    AITags: string;
    [key: string]: string; // for metadata fields
}

export const ExportFolderCSV = () => {
    const [folderPath, setFolderPath] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [includeSubfolders, setIncludeSubfolders] = React.useState(false);
    const [status, setStatus] = React.useState('');
    
    // Add mounted ref to track component mount state
    const mounted = React.useRef(true);

    // Add cleanup effect
    React.useEffect(() => {
        return () => {
            mounted.current = false;
        };
    }, []);

    // Create safe setState function
    const safeSetStatus = (message: string) => {
        if (mounted.current) {
            setStatus(message);
        }
    };

    //CSV Headers contain some basic file fields and all metadata fields (even if they are empty for a file)
    const getCSVHeaders = async () => {
        const metadataResponse = await fetch('/api/v1/customMetadataFields', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!metadataResponse.ok) {
            throw new Error('Failed to fetch custom metadata fields');
        }

        const metadataFields = await metadataResponse.json();
        const metadataHeaders = metadataFields.map(field => `custom_metadata_${field.name}`);
        const baseHeaders = ['fileId', 'name', 'folderPath', 'filePath', 'createdAt', 'tags', 'AITags'];
        return [...baseHeaders, ...metadataHeaders];
    };

    //function to generate and download the CSV file using headers and file data.
    const generateAndDownloadCSV = (fileObjects: FileObject[], headers: string[], description: string) => {
        const csvContent = [
            headers.join(','),
            ...fileObjects.map(obj => headers.map(header => obj[header] || '').join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `files_export_${Math.floor(Date.now() / 1000)}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);

        safeSetStatus(`Successfully exported ${fileObjects.length} ${description}`);
    };

    //function to export files from a single folder without subfolders.
    const handleExportWithoutSubfolders = async (folderPath: string, generateCSV: boolean = true) => {
        try {
            // Fetch files
            let allFiles: FileResponse[] = [];
            let skip = 0;
            const limit = 100;
            const MAX_FILES = 10000;
            
            //fetch files from the folder recursively upto max limit
            while (allFiles.length < MAX_FILES) {
                const encodedPath = encodeURIComponent(folderPath);
                const response = await fetch(
                    `/api/v2/files?skip=${skip}&limit=${limit}&sort=DESC_CREATED&searchQuery=type%3D%22file%22&path=${encodedPath}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch files');
                }

                const data = await response.json();
                allFiles = [...allFiles, ...data];

                safeSetStatus(`Exported ${allFiles.length} files...`);
                
                if (data.length < limit) break;
                if (allFiles.length >= MAX_FILES) {
                    safeSetStatus(`Reached maximum limit of ${MAX_FILES} files`);
                    break;
                }
                skip += limit;
            }

            // Create array of objects with all fields
            const fileObjects = allFiles.map(file => {
                const baseObject = {
                    fileId: file.fileId,
                    name: file.name,
                    folderPath: file.filePath.replace(file.name, ''),
                    filePath: file.filePath,
                    createdAt: file.createdAt,
                    tags: (file.tags || []).join(';'),
                    AITags: (file.AITags || []).join(';')
                };

                // Add metadata fields if they exist
                if (file.customMetadata) {
                    Object.entries(file.customMetadata).forEach(([key, value]) => {
                        baseObject[`custom_metadata_${key}`] = Array.isArray(value) 
                            ? value.join(';') 
                            : String(value);
                    });
                }

                return baseObject;
            });

            if (generateCSV) {
                safeSetStatus('Fetching metadata fields...');
                const allHeaders = await getCSVHeaders();
                generateAndDownloadCSV(fileObjects, allHeaders, 'files');
            }

            return fileObjects;
        } catch (error) {
            safeSetStatus(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
            throw error;
        }
    };

    const handleExportWithSubfolders = async (folderPath: string) => {
        const MAX_FOLDERS = 10;
        const limit = 100;
        let foldersToProcess = [folderPath];
        let processedFolders = new Set<string>();
        
        // First, collect all folders
        while (foldersToProcess.length > 0 && processedFolders.size < MAX_FOLDERS) {
            const currentFolder = foldersToProcess.shift()!;
            if (processedFolders.has(currentFolder)) continue;

            safeSetStatus(`Discovering folders: ${processedFolders.size + 1}/${MAX_FOLDERS}`);
            const encodedPath = encodeURIComponent(currentFolder);
            const folderResponse = await fetch(
                `/api/v2/files?skip=0&limit=${limit}&sort=DESC_CREATED&searchQuery=type%3D%22folder%22&path=${encodedPath}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!folderResponse.ok) {
                throw new Error(`Failed to fetch folders for path: ${currentFolder}`);
            }

            const folderData = await folderResponse.json() as FolderResponse[];
            const newFolders = folderData
                .map(folder => folder.folderPath)
                .filter(path => !processedFolders.has(path));
            
            foldersToProcess.push(...newFolders);
            processedFolders.add(currentFolder);
        }

        // Then, process files for all discovered folders
        let allFileObjects: FileObject[] = [];
        let folderCount = 0;
        for (const folder of processedFolders) {
            folderCount++;
            safeSetStatus(`Processing files from folder ${folderCount}/${processedFolders.size}`);
            const fileObjects = await handleExportWithoutSubfolders(folder, false);
            allFileObjects.push(...fileObjects);
        }

        // Generate single CSV for all files
        const allHeaders = await getCSVHeaders();
        generateAndDownloadCSV(allFileObjects, allHeaders, `files from ${processedFolders.size} folders`);

        return allFileObjects;
    };

    const handleExport = async () => {
        if (!folderPath) {
            alert('Please enter a folder path');
            return;
        }

        setIsLoading(true);

        try {
            if (includeSubfolders) {
                await handleExportWithSubfolders(folderPath.trim());
            } else {
                await handleExportWithoutSubfolders(folderPath.trim());
            }
            safeSetStatus('Export completed successfully');
        } catch (error) {
            safeSetStatus(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box>
            <VStack spacing={4} align={'stretch' as const}>
                <Input
                    placeholder="Enter folder path"
                    value={folderPath}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFolderPath(e.target.value)}
                />
                <Checkbox
                    isChecked={includeSubfolders}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeSubfolders(e.target.checked)}
                >
                    <ChakraText>Include subfolders</ChakraText>
                </Checkbox>
                <Button
                    onClick={handleExport}
                    isLoading={isLoading}
                    loadingText={status}
                    disabled={!folderPath || isLoading}
                >
                    Export CSV
                </Button>
                {status && (
                    <ChakraText color="gray.600" fontSize="sm">
                        {status}
                    </ChakraText>
                )}
            </VStack>
        </Box>
    );
};
